import { useState } from "react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserPlus, LogIn, Eye, EyeOff, CheckCircle2 } from "lucide-react";

export const StepClientRegistration = ({ registrationData, setRegistrationData, user, applicationId, applicationRef }) => {
  const [showPassword, setShowPassword] = useState(false);

  // Already authenticated — show a friendly confirmation, no fields
  if (user) {
    // HF-Apply-8 (2026-06-24) — switch wording from "start" to "resume"
    // when the user has an in-progress draft, so they don't think
    // they're about to wipe their previous work. Also surface the
    // application reference inline so the connection is obvious.
    const isResuming = Boolean(applicationId);
    return (
      <div className="space-y-6" data-testid="step-registration-already-signed-in">
        <div className="border-b border-gray-200 pb-6">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2 flex items-center gap-3">
            <CheckCircle2 className="w-6 h-6 text-green-600" />
            Account
          </h2>
          <p className="text-sm text-gray-600">
            You're signed in as <span className="font-semibold">{user.name}</span> (<span className="font-mono">{user.email}</span>).
            {isResuming ? (
              <>
                {" "}Click <span className="font-semibold">Continue</span> to resume your application
                {applicationRef ? <> <span className="font-mono text-[#bd9a59]">({applicationRef})</span></> : null}.
              </>
            ) : (
              <> Click <span className="font-semibold">Continue</span> to start your application.</>
            )}
          </p>
          {/* HF-Apply-8 — explicit loading hint when the user is signed in
              but the draft hasn't hydrated yet. Pairs with the Continue
              button being disabled in this state (see LoanApplicationForm.jsx). */}
          {!applicationId && (
            <p className="text-xs text-gray-400 mt-2 italic">Loading your draft…</p>
          )}
        </div>
      </div>
    );
  }

  const setField = (field, value) => {
    setRegistrationData((prev) => ({ ...prev, [field]: value }));
  };

  const isSignIn = registrationData.signin_mode;

  return (
    <div className="space-y-6" data-testid="step-registration-form">
      <div className="border-b border-gray-200 pb-6">
        <h2 className="text-2xl font-semibold text-gray-900 mb-2 flex items-center gap-3">
          {isSignIn ? <LogIn className="w-6 h-6 text-[#bd9a59]" /> : <UserPlus className="w-6 h-6 text-[#bd9a59]" />}
          {isSignIn ? "Sign In" : "Create Your Client Account"}
        </h2>
        <p className="text-sm text-gray-600">
          {isSignIn
            ? "Sign in with your existing Solara client account to continue your application."
            : "Set up your client account so you can track your application progress, upload documents, and access your private portal."}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {!isSignIn && (
          <div className="md:col-span-2">
            <Label htmlFor="reg-full-name">Full Name *</Label>
            <Input
              id="reg-full-name"
              data-testid="reg-full-name"
              value={registrationData.full_name}
              onChange={(e) => setField("full_name", e.target.value)}
              placeholder="Jane Smith"
            />
          </div>
        )}

        <div className={isSignIn ? "md:col-span-2" : ""}>
          <Label htmlFor="reg-email">Email *</Label>
          <Input
            id="reg-email"
            data-testid="reg-email"
            type="email"
            value={registrationData.email}
            onChange={(e) => setField("email", e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
          />
        </div>

        {!isSignIn && (
          <div>
            <Label htmlFor="reg-phone">Phone *</Label>
            <Input
              id="reg-phone"
              data-testid="reg-phone"
              type="tel"
              value={registrationData.phone}
              onChange={(e) => setField("phone", e.target.value)}
              placeholder="0400 000 000"
              autoComplete="tel"
            />
          </div>
        )}

        <div className={isSignIn ? "md:col-span-2" : ""}>
          <Label htmlFor="reg-password">Password *</Label>
          <div className="relative">
            <Input
              id="reg-password"
              data-testid="reg-password"
              type={showPassword ? "text" : "password"}
              value={registrationData.password}
              onChange={(e) => setField("password", e.target.value)}
              placeholder={isSignIn ? "Your password" : "At least 8 characters, with a letter and a digit"}
              autoComplete={isSignIn ? "current-password" : "new-password"}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              data-testid="reg-toggle-password-visibility"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {isSignIn && (
            <div className="mt-1 text-right">
              <Link to="/forgot-password" className="text-xs text-[#bd9a59] hover:underline" data-testid="reg-forgot-password-link">
                Forgot your password?
              </Link>
            </div>
          )}
        </div>

        {!isSignIn && (
          <div>
            <Label htmlFor="reg-confirm-password">Confirm Password *</Label>
            <Input
              id="reg-confirm-password"
              data-testid="reg-confirm-password"
              type={showPassword ? "text" : "password"}
              value={registrationData.confirm_password}
              onChange={(e) => setField("confirm_password", e.target.value)}
              placeholder="Re-enter password"
              autoComplete="new-password"
            />
          </div>
        )}

        {!isSignIn && (
          <div className="md:col-span-2">
            <Label htmlFor="reg-company">Company / Borrower Name <span className="text-gray-400 font-normal">(optional)</span></Label>
            <Input
              id="reg-company"
              data-testid="reg-company"
              value={registrationData.company_name}
              onChange={(e) => setField("company_name", e.target.value)}
              placeholder="Acme Pty Ltd"
            />
            <p className="text-xs text-gray-500 mt-1">
              If you're applying on behalf of a company, you can enter its name here. We'll pre-fill it on the next step.
            </p>
          </div>
        )}
      </div>

      <div className="pt-4 border-t border-gray-200">
        <p className="text-sm text-gray-600">
          {isSignIn ? (
            <>
              Don't have an account yet?{" "}
              <button
                type="button"
                onClick={() => setField("signin_mode", false)}
                className="text-[#bd9a59] font-medium hover:underline"
                data-testid="reg-toggle-register"
              >
                Create one
              </button>
            </>
          ) : (
            <>
              Already have a Solara client account?{" "}
              <button
                type="button"
                onClick={() => setField("signin_mode", true)}
                className="text-[#bd9a59] font-medium hover:underline"
                data-testid="reg-toggle-signin"
              >
                Sign in instead
              </button>
            </>
          )}
        </p>
      </div>
    </div>
  );
};
